import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { SiteDashboardService } from '../../site-dashboard.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DecimalPipe } from '@angular/common';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-daily-charger-usage-report',
	templateUrl: './daily-charger-usage-report.component.html',
	styleUrls: ['./daily-charger-usage-report.component.css']
})
export class DailyChargerUsageReportComponent implements OnInit, OnDestroy {
	siteId = 0;

	siteSubscription: Subscription = new Subscription();
	siteInfo: any = {};

	maxDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // yesterday
	toDate = this.maxDate; // yesterday
	fromDate = new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	minDate = this.fromDate;

	columnConfig: ColumnsConfig[] = [
		{key: 'day', name: this.translate.instant('g.date'), type: 'date'},
		{key: 'chargers_count', name: this.translate.instant('fleet_reports.chargers_count'), type: 'number'},
		{key: 'used_chargers_count', name: this.translate.instant('fleet_reports.used_chargers_count'), type: 'number', hasTooltip: this.translate.instant('tooltip.chargLinkDevice.site_usage_used_chargers_count_column')},
		{key: 'usage_score', name: this.translate.instant('fleet_reports.utilization_score_percent'), type: 'number', hasTooltip: this.translate.instant('tooltip.chargLinkDevice.site_usage_utilization_score_column')},
		{key: 'total_daily_charge_hour', name: this.translate.instant('fleet_reports.total_daily_charge_duration'), type: 'number'},
		{key: 'total_daily_idle_hour', name: this.translate.instant('fleet_reports.total_daily_idle_hours'), type: 'number'},
		{key: 'total_daily_off_hour', name: this.translate.instant('fleet_reports.total_daily_off_hours'), type: 'number'},
		{key: 'total_daily_ah', name: this.translate.instant('fleet_reports.total_daily_ah'), type: 'number'},
		{key: 'total_daily_kwh', name: this.translate.instant('fleet_reports.total_daily_kwh'), type: 'number'},
		{key: 'max_kw', name: this.translate.instant('fleet_reports.max_kw'), type: 'number', hasTooltip: this.translate.instant('tooltip.chargLinkDevice.site_usage_max_kw_column')},
	];

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		hideNoData: true,
		draggable: true,
		fileName: this.translate.instant('fleet_reports.daily_charger_usage_report'),
		csvFileHeaderInfo: []
	};

	constructor(
		private sitesService: SitesService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private siteDashboardService: SiteDashboardService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		this.siteId = this.route.parent.snapshot.params.siteId || 0;

		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.siteInfo = siteInfo;
				this.minDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
				if (this.minDate > this.fromDate)
					this.fromDate = this.minDate;

				this.getDailyChargerUsageReport();
			}
		});
	}

	changeDate(field, value) {
		this[field] = value;

		this.getDailyChargerUsageReport();
	}

	getDailyChargerUsageReport() {
		const zoneDiff	= new Date().getTimezoneOffset() * -1;
		let fromDate:any	= new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate:any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		this.sitesService.getDailyChargerUsageReport(fromDate, toDate, this.siteId).subscribe((res: any) => {
			const tableData = [];
			const decimalPipe: DecimalPipe = new DecimalPipe("en-US");

			for (const day in res) {
				if (res.hasOwnProperty(day)) {
					tableData.push({
						day: {value: res[day].day},
						chargers_count: {value: res[day].chargers_count},
						used_chargers_count: {value: res[day].used_chargers_count},
						usage_score: {value: this.siteDashboardService.calculateUsageScore(res[day].chargers_count, res[day].used_chargers_count)},
						total_daily_charge_hour: {value: Math.round((res[day].total_charge_second || 0) / 3600).toLocaleString()},
						total_daily_idle_hour: {value: Math.round((res[day].total_idle_second || 0) / 3600).toLocaleString()},
						total_daily_off_hour: {value: Math.round((res[day].total_off_time_second || 0) / 3600).toLocaleString()},
						total_daily_ah: {value: Math.round((res[day].total_as || 0) / 3600).toLocaleString()},
						total_daily_kwh: {value: Math.round((res[day].total_ws || 0) / (3600 * 1000)).toLocaleString()},
						max_kw: {value: decimalPipe.transform(res[day].max_kw || 0, '1.0-1')},
					});
				}
			}

			this.tableData = tableData;
			this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
				customerName: this.siteInfo.customer_name,
				siteName: this.siteInfo.name,
				includeSpacing: false
			});
		});
	}

	ngOnDestroy() {
		this.siteSubscription.unsubscribe();
	}
}
